import { z } from 'zod';
import { validateCPF, validateCNPJ } from '../../../utils/validate';
import { MaskCNPJ, MaskCPF } from '../../../utils/mask';
import {
  CashMeCollateralType,
  CashMeIndex,
  CashMeInterestTable,
  CashMeMaritalStatus,
  CashMeMonetaryCorrection,
  ICashMeLimiterConditional,
  ICashMeLimiters,
} from './types';
import {
  CASHME_MIN_GRACE_PERIOD,
  CASHME_MIN_MAIN_MONTHLY_INCOME,
} from './constants';
import { isValidMobile, MOBILE_FORMAT_MESSAGE } from './phone';

const onlyDigits = (value: string) => value.replace(/\D/g, '');

/* The shared validators split on the mask punctuation and throw on bare
   digits or a partially typed value, so gate on length and rebuild the
   canonical mask before calling them. */
const isValidCpf = (value: string) => {
  const digits = onlyDigits(value);
  return digits.length === 11 && validateCPF(MaskCPF(digits));
};

const isValidCnpj = (value: string) => {
  const digits = onlyDigits(value);
  return digits.length === 14 && validateCNPJ(MaskCNPJ(digits));
};

const MARITAL_STATUSES: CashMeMaritalStatus[] = [
  'SINGLE',
  'MARRIED',
  'DIVORCED',
  'WIDOWER',
  'SEPARATED',
  'COHABITATION',
];

export function maritalRules(maritalStatus?: string, cohabitation?: string) {
  const isMarried =
    maritalStatus === 'MARRIED' || maritalStatus === 'COHABITATION';
  const asksCohabitation = !!maritalStatus && !isMarried;
  const needsPartner = isMarried || cohabitation === 'true';

  return {
    asksCohabitation,
    needsPartner,
    partnerTitle:
      maritalStatus === 'MARRIED'
        ? 'Dados do cônjuge'
        : 'Dados do(a) companheiro(a)',
  };
}

export function ageInMonthsAt(birthDate: string, reference = new Date()) {
  const [year, month, day] = birthDate.split('-').map(Number);
  if (!year || !month || !day) return null;

  let months =
    (reference.getFullYear() - year) * 12 + (reference.getMonth() + 1 - month);

  if (reference.getDate() < day) months -= 1;

  return months;
}

const cpfField = z
  .string()
  .min(1, 'Informe o CPF')
  .refine(isValidCpf, 'Informe um CPF válido');

const emailField = z
  .string()
  .min(1, 'Informe o e-mail')
  .email('E-mail inválido');

const mobileField = z
  .string()
  .min(1, 'Informe o celular')
  .refine(isValidMobile, MOBILE_FORMAT_MESSAGE);

const isoDateField = z
  .string()
  .min(1, 'Informe a data')
  .refine((value) => !Number.isNaN(new Date(value).getTime()), 'Data inválida');

const zipCodeField = z
  .string()
  .min(1, 'Informe o CEP')
  .refine((value) => onlyDigits(value).length === 8, 'Informe um CEP válido');

export const addressSchema = z.object({
  zipCode: zipCodeField,
  number: z.string().min(1, 'Informe o número'),
  street: z.string().min(1, 'Informe o logradouro'),
  district: z.string().min(1, 'Informe o bairro'),
  city: z.string().min(1, 'Informe a cidade'),
  state: z.string().min(1, 'Informe o estado'),
});

/* The address block is hidden when the borrower reuses the collateral address,
   but RHF still holds its empty values — validating it unconditionally would
   block submit with errors on fields that are not on screen. */
const looseAddressSchema = z.object({
  zipCode: z.string().optional(),
  number: z.string().optional(),
  street: z.string().optional(),
  district: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
});

function requireAddressUnlessReused(
  data: { address?: unknown; useCollateralAddress: boolean },
  ctx: z.RefinementCtx
) {
  if (data.useCollateralAddress) return;

  const result = addressSchema.safeParse(data.address ?? {});
  if (!result.success) {
    result.error.issues.forEach((issue) => {
      ctx.addIssue({
        code: 'custom',
        path: ['address', ...issue.path],
        message: issue.message,
      });
    });
  }
}

const brl = (value: number) => `R$ ${value.toLocaleString('pt-BR')}`;

export function findConditional(
  limiters: ICashMeLimiters,
  interestTable?: CashMeInterestTable,
  monetaryCorrection?: CashMeMonetaryCorrection,
  index?: CashMeIndex
): ICashMeLimiterConditional | undefined {
  return limiters.conditionals.find(
    (conditional) =>
      conditional.interestTable === interestTable &&
      conditional.monetaryCorrection === monetaryCorrection &&
      (conditional.indexType ?? undefined) === (index ?? undefined)
  );
}

export function buildSimulationSchema(limiters: ICashMeLimiters) {
  return z
    .object({
      personType: z.enum(['natural', 'legal']),
      name: z.string().min(1, 'Informe o nome'),
      document: z.string().min(1, 'Informe o documento'),
      email: emailField,
      birthDate: z.string().optional(),
      phone: mobileField,
      loanAmount: z
        .number({ message: 'Informe o valor do empréstimo' })
        .min(
          limiters.minCreditValue,
          `O valor mínimo é ${brl(limiters.minCreditValue)}`
        ),
      propertyAmount: z
        .number({ message: 'Informe o valor do imóvel' })
        .min(
          limiters.minPropertyValue,
          `O imóvel deve valer no mínimo ${brl(limiters.minPropertyValue)}`
        ),
      propertyState: z
        .string()
        .min(1, 'Informe o estado do imóvel')
        .refine(
          (state) => !limiters.statesNotAccepted.includes(state),
          'Ainda não atendemos imóveis neste estado'
        ),
      interestTable: z.enum(['PRICE', 'SAC']),
      installmentPeriod: z.number().int(),
      gracePeriod: z.number().int(),
      monetaryCorrection: z.enum(['PRE_FIXED', 'POST_FIXED']),
      index: z.enum(['IPCA', 'CDI']).optional(),
    })
    .superRefine((data, ctx) => {
      if (data.loanAmount >= data.propertyAmount) {
        ctx.addIssue({
          code: 'custom',
          path: ['loanAmount'],
          message: 'O empréstimo deve ser menor que o valor do imóvel',
        });
      }

      if (
        data.gracePeriod < CASHME_MIN_GRACE_PERIOD ||
        data.gracePeriod > limiters.graceMonthsTerm
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['gracePeriod'],
          message: `A carência deve ficar entre ${CASHME_MIN_GRACE_PERIOD} e ${limiters.graceMonthsTerm} meses`,
        });
      }

      const conditional = findConditional(
        limiters,
        data.interestTable,
        data.monetaryCorrection,
        data.index
      );

      if (!conditional) {
        ctx.addIssue({
          code: 'custom',
          path: ['monetaryCorrection'],
          message: 'Essa combinação de condições não está disponível',
        });
      } else if (
        data.installmentPeriod < conditional.minTerm ||
        data.installmentPeriod > conditional.maxTerm
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['installmentPeriod'],
          message: `Para essa condição, o prazo deve ficar entre ${conditional.minTerm} e ${conditional.maxTerm} meses`,
        });
      }

      if (data.personType === 'natural') {
        if (!isValidCpf(data.document)) {
          ctx.addIssue({
            code: 'custom',
            path: ['document'],
            message: 'Informe um CPF válido',
          });
        }

        if (!data.birthDate) {
          ctx.addIssue({
            code: 'custom',
            path: ['birthDate'],
            message: 'Informe a data de nascimento',
          });
          return;
        }

        const months = ageInMonthsAt(data.birthDate);

        if (months === null) {
          ctx.addIssue({
            code: 'custom',
            path: ['birthDate'],
            message: 'Data inválida',
          });
          return;
        }

        if (months < limiters.minAge * 12) {
          ctx.addIssue({
            code: 'custom',
            path: ['birthDate'],
            message: `É necessário ter ao menos ${limiters.minAge} anos`,
          });
        }

        if (
          months + data.installmentPeriod + data.gracePeriod >
          limiters.maxAge * 12
        ) {
          ctx.addIssue({
            code: 'custom',
            path: ['installmentPeriod'],
            message: `O financiamento precisa terminar antes dos ${limiters.maxAge} anos`,
          });
        }
      } else {
        if (!isValidCnpj(data.document)) {
          ctx.addIssue({
            code: 'custom',
            path: ['document'],
            message: 'Informe um CNPJ válido',
          });
        }
      }
    });
}

export type SimulationFormValues = z.input<
  ReturnType<typeof buildSimulationSchema>
>;

export function buildCollateralStepSchema(limiters: ICashMeLimiters) {
  return z
    .object({
      address: addressSchema,
      useMainProponentAddress: z.boolean(),
      type: z.enum(
        ['APARTMENT', 'HOUSE', 'COMMERCIAL_PROPERTY', 'LAND', 'PARKING_SPOT'],
        { message: 'Selecione o tipo de imóvel' }
      ),
      value: z
        .number({ message: 'Informe o valor do imóvel' })
        .min(
          limiters.minPropertyValue,
          `O imóvel deve valer no mínimo ${brl(limiters.minPropertyValue)}`
        ),
      financed: z.boolean(),
      financedBank: z.string().optional(),
      financedDebitBalance: z.number().optional(),
    })
    .superRefine((data, ctx) => {
      if (data.financed && !data.financedBank) {
        ctx.addIssue({
          code: 'custom',
          path: ['financedBank'],
          message: 'Informe o banco do financiamento',
        });
      }
    });
}

export type CollateralStepValues = z.input<
  ReturnType<typeof buildCollateralStepSchema>
>;

export const spouseSchema = z.object({
  name: z.string().min(1, 'Informe o nome completo'),
  document: cpfField,
  email: emailField,
  phone: mobileField,
  birthDate: isoDateField,
});

export const proponentStepSchema = z
  .object({
    address: looseAddressSchema,
    useCollateralAddress: z.boolean(),
    maritalStatus: z.string().optional(),
    matrimonialRegime: z.string().optional(),
    cohabitation: z.string().optional(),
    spouse: z
      .object({
        name: z.string().optional(),
        document: z.string().optional(),
        email: z.string().optional(),
        phone: z.string().optional(),
        birthDate: z.string().optional(),
      })
      .optional(),
  })
  .superRefine((data, ctx) => {
    requireAddressUnlessReused(data, ctx);

    if (
      !data.maritalStatus ||
      !MARITAL_STATUSES.includes(data.maritalStatus as CashMeMaritalStatus)
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['maritalStatus'],
        message: 'Informe o estado civil',
      });
      return;
    }

    if (data.maritalStatus === 'MARRIED' && !data.matrimonialRegime) {
      ctx.addIssue({
        code: 'custom',
        path: ['matrimonialRegime'],
        message: 'Informe o regime de bens',
      });
    }

    const { asksCohabitation, needsPartner } = maritalRules(
      data.maritalStatus,
      data.cohabitation
    );

    if (
      asksCohabitation &&
      data.cohabitation !== 'true' &&
      data.cohabitation !== 'false'
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['cohabitation'],
        message: 'Informe se vive em união estável',
      });
    }

    if (needsPartner) {
      const result = spouseSchema.safeParse(data.spouse ?? {});

      if (!result.success) {
        result.error.issues.forEach((issue) => {
          ctx.addIssue({
            code: 'custom',
            path: ['spouse', ...issue.path],
            message: issue.message,
          });
        });
      }
    }
  });

export type ProponentStepValues = z.input<typeof proponentStepSchema>;

export const shareholdingSchema = z.object({
  document: z
    .string()
    .min(1, 'Informe o CPF ou CNPJ')
    .refine(
      (value) => isValidCpf(value) || isValidCnpj(value),
      'Informe um CPF ou CNPJ válido'
    ),
  percentage: z
    .number({ message: 'Informe a participação' })
    .int('Use um número inteiro')
    .min(0, 'Mínimo 0%')
    .max(100, 'Máximo 100%'),
});

export const shareholdingsTotal = (
  shareholdings: { percentage?: number }[] = []
) =>
  shareholdings.reduce((total, item) => {
    const percentage = item.percentage ?? 0;
    return total + (Number.isFinite(percentage) ? percentage : 0);
  }, 0);

export const companyStepSchema = z
  .object({
    address: looseAddressSchema,
    useCollateralAddress: z.boolean(),
    companyType: z.string().min(1, 'Informe o tipo de empresa'),
    responsibleName: z.string().optional(),
    annualBilling: z.string().optional(),
    shareholdings: z
      .array(shareholdingSchema)
      .min(1, 'Cadastre ao menos um sócio'),
  })
  .superRefine((data, ctx) => {
    requireAddressUnlessReused(data, ctx);

    /* Under 100% is only a CashMe WARNING, so the form lets it through with a
       notice; over 100% can never be valid. */
    if (shareholdingsTotal(data.shareholdings) > 100) {
      ctx.addIssue({
        code: 'custom',
        path: ['shareholdings'],
        message: 'A soma das participações não pode passar de 100%',
      });
    }
  });

export type CompanyStepValues = z.input<typeof companyStepSchema>;

export const incomeStepSchema = z.object({
  monthlyIncome: z
    .number({ message: 'Informe a renda mensal' })
    .min(
      CASHME_MIN_MAIN_MONTHLY_INCOME,
      `A renda mensal mínima é R$ ${CASHME_MIN_MAIN_MONTHLY_INCOME.toLocaleString('pt-BR')}`
    ),
  incomeType: z.string().optional(),
  creditPurpose: z.string().min(1, 'Informe a finalidade do crédito'),
});

export type IncomeStepValues = z.input<typeof incomeStepSchema>;

export function ltvCap(
  limiters: ICashMeLimiters,
  conditional: ICashMeLimiterConditional | undefined,
  collateralType: CashMeCollateralType
): number {
  const propertyCap = limiters.propertyMaxLtv[collateralType] ?? 0;
  return conditional ? Math.min(propertyCap, conditional.maxLtv) : propertyCap;
}

export function exceedsLtv(
  loanAmount: number,
  cap: number,
  collateralValue: number
): boolean {
  return loanAmount > (collateralValue * cap) / 100;
}

export function ltvMessage(cap: number, collateralValue: number): string {
  const max = (collateralValue * cap) / 100;
  return `Para este imóvel o crédito é limitado a ${cap}% do valor — no máximo ${brl(max)}`;
}
